import { Select, SelectItem, SelectSkeleton } from "@carbon/react";
import { useQuery, gql, useLazyQuery } from "@apollo/client";
import React, { useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import leftIcon from '@iconify/icons-solar/arrow-left-linear';
import rightIcon from '@iconify/icons-solar/arrow-right-linear';
import undoIcon from '@iconify/icons-solar/undo-left-linear';

const YEAR_QUERY = gql`
    query GetSchoolYearOverview {
        schoolYearOverview {
            weeks {
                weekNumber
                startDate
                endDate
            }
        }
    }
`;

const TODAY_QUERY = gql`
    query GetToday {
        today {
            weekNumber
        }
    }
`;

function formatDateRange(startDate, endDate, weekNumber) {
    // Assumes startDate/endDate are ISO strings
    const start = new Date(startDate);
    const end = new Date(endDate);
    const pad = (n) => n.toString().padStart(2, "0");
    const format = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    return `${format(start)} - ${format(end)} (Woche ${weekNumber+1})`;
}

export default function SchoolWeekSelector({ selectedWeek = null, onWeekChange, label = true }) {
    const { data, loading, error } = useQuery(YEAR_QUERY);
    const [fetchToday, { data: todayData }] = useLazyQuery(TODAY_QUERY);

    useEffect(() => {
        fetchToday();
    }, [fetchToday]);

    useEffect(() => {
        if (todayData && todayData.today && data && data.schoolYearOverview.weeks.length > 0 && selectedWeek == null) {
            const todayWeekNumber = todayData.today.weekNumber;
            const todayWeek = data.schoolYearOverview.weeks.find(w => w.weekNumber === todayWeekNumber);
            if (todayWeek) {
                onWeekChange && onWeekChange(todayWeek.weekNumber);
            }
        }
    }, [todayData, data, selectedWeek, onWeekChange]);

    // Do not auto-set week 0 as a fallback. Leave selectedWeek === null until
    // either today's week is available (handled above) or the user explicitly
    // selects a week. This prevents writing `?week=0` into the URL by default.
    // The selector will call onWeekChange when the user picks a value.

    if (loading) return <SelectSkeleton />;
    if (error) return <div>Fehler beim Laden der Wochen: {error.message}</div>;

    const weeks = data.schoolYearOverview.weeks;

    return (
        <div className=" w-full md:w-1/2">
            {label && <label className="block mb-2 text-sm font-medium text-gray-700">Schulwoche auswählen</label>}
            <div className="flex items-center w-full">
                <button
                    type="button"
                    className="hidden md:block p-2 disabled:opacity-50 disabled:cursor-not-allowed mainBtn"
                    disabled={selectedWeek == null || selectedWeek >= weeks[weeks.length - 1].weekNumber}
                    onClick={() => {
                        const currentWeek = todayData?.today?.weekNumber;
                        if (currentWeek != null) {
                            onWeekChange && onWeekChange(currentWeek);
                        }
                    }}
                    aria-label="Aktuelle Woche"
                >
                    <Icon icon={undoIcon}  width="24" height="24"/>
                </button>
                <div className="w-1"/>
                <button
                    type="button"
                    className="hidden md:block p-2 disabled:opacity-50 disabled:cursor-not-allowed mainBtn"
                    disabled={selectedWeek == null || selectedWeek <= weeks[0].weekNumber}
                    onClick={() => {
                        const currentIdx = weeks.findIndex(w => w.weekNumber === selectedWeek);
                        if (currentIdx > 0) {
                            onWeekChange && onWeekChange(weeks[currentIdx - 1].weekNumber);
                        }
                    }}
                    aria-label="Vorherige Woche"
                >
                    <Icon icon={leftIcon} width="24" height="24"/>
                </button>
                <Select
                    id="week-select"
                    size="xl"
                    className="w-full md:w-96"
                    value={selectedWeek ?? ""}
                    onChange={e => onWeekChange && onWeekChange(Number(e.target.value))}
                    hideLabel
                >
                    {weeks.map((week) => (
                        <SelectItem
                            key={week.weekNumber}
                            value={week.weekNumber}
                            text={formatDateRange(week.startDate, week.endDate, week.weekNumber)}
                        />
                    ))}
                </Select>
                <button
                    type="button"
                    className="hidden md:block p-2 disabled:opacity-50 disabled:cursor-not-allowed mainBtn"
                    disabled={selectedWeek == null || selectedWeek >= weeks[weeks.length - 1].weekNumber}
                    onClick={() => {
                        const currentIdx = weeks.findIndex(w => w.weekNumber === selectedWeek);
                        if (currentIdx < weeks.length - 1) {
                            onWeekChange && onWeekChange(weeks[currentIdx + 1].weekNumber);
                        }
                    }}
                    aria-label="Nächste Woche"
                >
                    <Icon icon={rightIcon}  width="24" height="24"/>
                </button>
                
            </div>
        </div>
    );
}